import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DataTable from '../shared/DataTable';
import CreateCatalogueDialog from './CreateCatalogueDialog';
import { formatDate } from '../../utils';

const CataloguesListView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [catalogues, setCatalogues] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [driftCount, setDriftCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCatalogues = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/vendor-catalogues/');
      setCatalogues(response.data);
    } catch {
      setSnackbar({ open: true, message: t('catalogues.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCatalogues();
    axios
      .get('/api/vendor-catalogues/profiles/')
      .then((res) => setProfiles(res.data))
      .catch(() => {});
    axios
      .get('/api/parts/price-drift/', { params: { count_only: 'true' } })
      .then((res) => setDriftCount(res.data.count || 0))
      .catch(() => {});
  }, [fetchCatalogues]);

  const handleToggleActive = async (catalogue, event) => {
    event.stopPropagation();
    try {
      await axios.patch(`/api/vendor-catalogues/${catalogue.id}/`, {
        is_active: !catalogue.is_active,
      });
      fetchCatalogues();
    } catch {
      setSnackbar({ open: true, message: t('catalogues.updateFailed'), severity: 'error' });
    }
  };

  return (
    <Box data-testid="catalogues-page">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: 'text.primary', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {t('catalogues.title')}
          </Typography>
          {driftCount > 0 && (
            <Chip
              icon={<TrendingUpIcon />}
              label={t('catalogues.driftBadge', { count: driftCount })}
              color="warning"
              variant="outlined"
              onClick={() => navigate('/catalogues/price-drift')}
              data-testid="drift-badge"
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="add-catalogue-button"
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            '&:hover': { bgcolor: 'secondary.light' },
          }}
        >
          {t('catalogues.newCatalogue')}
        </Button>
      </Box>

      <DataTable
        columns={[
          {
            key: 'name',
            label: t('catalogues.name'),
            maxWidth: 240,
            render: (c) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.name}
              </Typography>
            ),
          },
          {
            key: 'vendor',
            label: t('catalogues.vendor'),
            render: (c) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{c.vendor?.name || '-'}</Typography>
                {c.vendor?.order_email && (
                  <Chip label={t('catalogues.emailOrderingChip')} size="small" variant="outlined" color="info" sx={{ height: 20, fontSize: '11px' }} />
                )}
              </Box>
            ),
          },
          {
            key: 'vendor_profile',
            label: t('catalogues.profile'),
            render: (c) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{c.vendor_profile_display}</Typography>
            ),
          },
          {
            key: 'item_count',
            label: t('catalogues.itemCount'),
            width: '1%',
            noWrap: true,
            render: (c) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {c.item_count ? c.item_count.toLocaleString('fi-FI') : '-'}
              </Typography>
            ),
          },
          {
            key: 'last_imported_at',
            label: t('catalogues.lastImported'),
            width: '1%',
            noWrap: true,
            render: (c) => (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                {c.last_imported_at ? formatDate(c.last_imported_at) : t('catalogues.notImported')}
              </Typography>
            ),
          },
          {
            key: 'is_active',
            label: t('catalogues.active'),
            width: '1%',
            render: (c) => (
              <Switch
                checked={c.is_active}
                onClick={(e) => handleToggleActive(c, e)}
                size="small"
                inputProps={{ 'data-testid': `catalogue-active-${c.id}` }}
              />
            ),
          },
        ]}
        data={catalogues}
        loading={loading}
        onRowClick={(c) => navigate(`/catalogues/${c.id}`)}
        emptyTitle={t('catalogues.empty')}
        testIdPrefix="catalogue-row"
        tableTestId="catalogues-table"
        alwaysVisible
        horizontalScroll
      />

      <CreateCatalogueDialog
        open={createOpen}
        handleClose={() => setCreateOpen(false)}
        profiles={profiles}
        existingNames={catalogues.map((c) => c.name)}
        onCreated={(catalogue) => {
          setSnackbar({ open: true, message: t('catalogues.created'), severity: 'success' });
          navigate(`/catalogues/${catalogue.id}`);
        }}
      />
    </Box>
  );
};

export default CataloguesListView;
